import React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";

type DrawerSide = "left" | "right";

export interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  side?: DrawerSide;
  title?: React.ReactNode;
  description?: React.ReactNode;
  widthClassName?: string;
  showCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
  closeOnEsc?: boolean;
  closeLabel?: string;
  overlayClassName?: string;
  contentClassName?: string;
  bodyClassName?: string;
  contentPositionClassName?: string;
  fallbackTitle?: string;
}

const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  children,
  side = "right",
  title,
  description,
  widthClassName = "max-w-xs",
  showCloseButton = false,
  closeOnOverlayClick = true,
  closeOnEsc = true,
  closeLabel = "Close",
  overlayClassName = "bg-black/40 dark:bg-black/60",
  contentClassName = "bg-bg-surface shadow-xl",
  bodyClassName = "flex h-full flex-col",
  contentPositionClassName,
  fallbackTitle = "Drawer",
}) => {
  const positionClassName =
    contentPositionClassName ??
    (side === "left"
      ? "inset-y-0 left-0 animate-in slide-in-from-left duration-300"
      : "inset-y-0 right-0 animate-in slide-in-from-right duration-300");

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={`fixed inset-0 z-50 animate-in fade-in duration-300 ${overlayClassName}`}
        />
        <Dialog.Content
          className={`fixed z-50 w-screen ${widthClassName} ${positionClassName} ${contentClassName}`}
          {...(!description ? { "aria-describedby": undefined } : {})}
          onInteractOutside={(event) => {
            if (!closeOnOverlayClick) {
              event.preventDefault();
            }
          }}
          onEscapeKeyDown={(event) => {
            if (!closeOnEsc) {
              event.preventDefault();
            }
          }}
        >
          <Dialog.Title className="sr-only">{title ?? fallbackTitle}</Dialog.Title>
          {description && (
            <Dialog.Description className="sr-only">
              {description}
            </Dialog.Description>
          )}
          {showCloseButton && (
            <Dialog.Close asChild>
              <button
                type="button"
                onClick={onClose}
                aria-label={closeLabel}
                className="absolute right-4 top-4 z-10 rounded-lg p-2 text-text-muted transition-colors hover:bg-bg-elevated hover:text-text-secondary"
              >
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          )}
          <div className={bodyClassName}>{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default Drawer;
